import React, { useEffect, useState } from 'react';
import { Tabs, Button, Spin, Typography, Modal, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconCalendarOff, IconCreditCard, IconReceipt2, IconRoute, IconFileInvoice, IconMessage } from '@tabler/icons-react';
import dayjs from 'dayjs';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import SegmentedPillToggle from '../../components/SegmentedPillToggle';
import NavBadge from '../../components/NavBadge';
import { actionButtonProps } from '../../constants/actionButtonProps';
import BookingActionsRow from '../../components/BookingActionsRow';
import ReviewModal from '../../components/ReviewModal';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import SaveCardStep from '../../components/SaveCardStep';
import ResumePaymentModal from '../../components/ResumePaymentModal';
import BookingCard from '../../components/BookingCard';
import BookingStatusFilter from '../../components/BookingStatusFilter';
import QuoteBreakdownSheet from '../../components/QuoteBreakdownSheet';
import IncomingBookings from './IncomingBookings';
import ActiveRentalCard from './ActiveRentalCard';
import { getMyBookings, cancelBooking, acceptQuoteBooking, cancelQuoteBooking, startBookingCardSetup } from '../../services/BookingService';
import { isServiceHoldPlaced, needsCardOnFile } from '../../helpers/serviceBookingHelper';
import { getInvoicesForBooking, downloadInvoice } from '../../services/InvoiceService';
import { IBooking, BookingStatus } from '../../interfaces/heavyDutyMachinery/IBooking';
import { TIMELINE_VISIBLE_STATUSES, CHAT_ENABLED_STATUSES } from '../../constants/bookingStatusOptions';
import { canOfferCancel } from '../../helpers/bookingHelpers';
import { canAcceptQuote } from '../../helpers/quoteHelper';
import { useAuth } from '../../contextProviders/AuthProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { useHostProfile } from '../../contextProviders/HostProfileProvider';
import { useIncomingBookingsBadge } from '../../contextProviders/IncomingBookingsProvider';
import { CORAL, CORAL_SOFT_BG } from '../../theme/creamyTokens';

const { Text } = Typography;

// Mobile-only pill tabs, replacing antd's underlined Tabs for Próximas/Pasadas
// -- active tab gets the brand's soft-tint background + strong text, inactive
// stays textual only, same "pill" language as the badges/toggle above.
const MobilePillTabs: React.FC<{ value: 'upcoming' | 'past'; onChange: (value: 'upcoming' | 'past') => void }> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const options: { value: 'upcoming' | 'past'; labelKey: string }[] = [
    { value: 'upcoming', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_TAB_UPCOMING },
    { value: 'past', labelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_TAB_PAST },
  ];
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            style={{
              border: 'none',
              borderRadius: 999,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              background: active ? CORAL_SOFT_BG : 'transparent',
              color: active ? CORAL : token.colorTextSecondary,
              transition: 'background-color 150ms ease, color 150ms ease',
            }}
          >
            {translate(t, option.labelKey)}
          </button>
        );
      })}
    </div>
  );
};

const MyBookings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isMobile } = useAppBreakpoint();
  const { hasHostProfile } = useHostProfile();
  const { unseenCount } = useIncomingBookingsBadge();
  const { userId } = useAuth();
  // Both sizes: guest and host bookings are one page with a toggle, not two
  // destinations. The phone's bottom bar has always worked that way (see
  // MobileBottomNav); desktop used to have two separate header links and now
  // has one entry in the header's host-tools menu (see HOST_TOOLS_NAV).
  //
  // Defaults to 'guest', except when arriving with ?view=host -- set by
  // MobileBottomNav when its "Reservas" tab is tapped while its coral badge
  // is showing an unseen incoming booking, so that tap actually lands on
  // the host view it's telling the host about, instead of always opening
  // guest bookings regardless of why the badge is lit.
  const [mobileView, setMobileView] = useState<'guest' | 'host'>(searchParams.get('view') === 'host' ? 'host' : 'guest');
  // Not isMobile-gated any more. Incoming bookings used to be a separate
  // top-level nav entry on desktop and a tab here on mobile -- the phone had
  // the better answer, and this is the whole reason the desktop header had
  // nine links. Same control, both sizes.
  const showHostToggle = hasHostProfile;
  const showingHostView = showHostToggle && mobileView === 'host';
  // Only drives the mobile pill tabs below -- desktop keeps antd's own
  // uncontrolled Tabs (defaulting to "upcoming"), untouched.
  const [mobileTab, setMobileTab] = useState<'upcoming' | 'past'>('upcoming');
  // Empty means no filter, every status shows. It always drove both sizes
  // (see filteredBookings below, which feeds the desktop list too) -- only
  // the control itself used to be hidden on desktop.
  const [statusFilter, setStatusFilter] = useState<BookingStatus[]>([]);
  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState<string | undefined>(undefined);
  const [reviewBookingId, setReviewBookingId] = useState<string | undefined>(undefined);
  const [cancelSheetBooking, setCancelSheetBooking] = useState<IBooking | undefined>(undefined);
  const [resumePaymentBookingId, setResumePaymentBookingId] = useState<string | undefined>(undefined);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | undefined>(undefined);
  // Reserva diferida, guest side -- see buildActions' own AwaitingQuote branch.
  const [quoteBooking, setQuoteBooking] = useState<IBooking | undefined>(undefined);
  const [cancelQuoteSheetBooking, setCancelQuoteSheetBooking] = useState<IBooking | undefined>(undefined);
  const [cancellingQuoteId, setCancellingQuoteId] = useState<string | undefined>(undefined);
  const [acceptingQuoteId, setAcceptingQuoteId] = useState<string | undefined>(undefined);

  const loadBookings = () => {
    setLoading(true);
    getMyBookings()
      .then(setBookings)
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_LOAD_ERROR)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = async (id: string) => {
    setCancellingId(id);
    try {
      const updated = await cancelBooking(id);
      setBookings((prev) => prev.map((b) => (b.id === id ? updated : b)));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_ERROR));
    } finally {
      setCancellingId(undefined);
    }
  };

  // The guest is only ever the recipient of the HostToGuest invoice (see
  // InvoicesController's own doc comment), so this list is always 0 or 1 --
  // no picker needed, just download whatever comes back.
  // Reserva diferida, guest side. Both mirror IncomingBookings' host-side
  // handlers: replace the row in place with whatever the server returns, so
  // the list reflects the new status without a full reload.
  // The card-saving step, open only between accepting a charge-at-handover
  // quote and confirming the card with Stripe.
  const [saveCardFor, setSaveCardFor] = useState<{ bookingId: string; clientSecret: string } | undefined>(undefined);
  const [startingCardSetupId, setStartingCardSetupId] = useState<string | undefined>(undefined);

  const handleAcceptQuote = async (id: string) => {
    setAcceptingQuoteId(id);
    try {
      const updated = await acceptQuoteBooking(id);
      setBookings((prev) => prev.map((b) => (b.id === id ? updated : b)));
      // A host who charges at handover asks for a card here and charges
      // nothing -- the guest has to confirm it with their bank ONCE, now,
      // while they are present, or the hold placed days from now gets
      // declined for want of anybody to answer a 3-D Secure prompt.
      if (updated.setupClientSecret) {
        setSaveCardFor({ bookingId: id, clientSecret: updated.setupClientSecret });
        return;
      }
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_ACCEPT_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_ACCEPT_ERROR));
    } finally {
      setAcceptingQuoteId(undefined);
    }
  };

  /**
   * Asks for this booking's own card-saving secret and opens the form.
   *
   * Its own round trip rather than something the booking already carried: the
   * professional is who confirmed the visit, and a secret handed to them would
   * be a secret for somebody else's card (see BookingController.CardSetup).
   */
  const handleStartCardSetup = async (id: string) => {
    setStartingCardSetupId(id);
    try {
      const updated = await startBookingCardSetup(id);
      if (!updated.setupClientSecret) throw new Error();
      setSaveCardFor({ bookingId: id, clientSecret: updated.setupClientSecret });
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SERVICE_SAVE_CARD_ERROR));
    } finally {
      setStartingCardSetupId(undefined);
    }
  };

  const handleCancelQuote = async (id: string) => {
    setCancellingQuoteId(id);
    try {
      const updated = await cancelQuoteBooking(id);
      setBookings((prev) => prev.map((b) => (b.id === id ? updated : b)));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_SUCCESS));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_ERROR));
    } finally {
      setCancellingQuoteId(undefined);
    }
  };

  const handleDownloadInvoice = async (bookingId: string) => {
    setDownloadingInvoiceId(bookingId);
    try {
      const invoices = await getInvoicesForBooking(bookingId);
      if (invoices.length === 0) {
        message.info(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_NOT_AVAILABLE));
        return;
      }
      await downloadInvoice(invoices[0]);
    } catch {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_DOWNLOAD_ERROR));
    } finally {
      setDownloadingInvoiceId(undefined);
    }
  };

  const filteredBookings = statusFilter.length > 0 ? bookings.filter((b) => statusFilter.includes(b.status)) : bookings;
  const now = dayjs();
  const upcoming = filteredBookings.filter((b) => dayjs(b.startAt).isAfter(now)).sort((a, b) => dayjs(a.startAt).diff(dayjs(b.startAt)));
  const past = filteredBookings.filter((b) => !dayjs(b.startAt).isAfter(now)).sort((a, b) => dayjs(b.startAt).diff(dayjs(a.startAt)));
  // Guest-only, deliberately not statusFilter-scoped -- an in-progress
  // rental (Confirmed, startAt <= now <= endAt) is a distinct situation
  // from browsing the upcoming/past lists below it, so it always shows
  // regardless of whatever status pills the guest has checked.
  const activeBooking = bookings.find((b) => b.status === 'Confirmed' && !dayjs(b.startAt).isAfter(now) && dayjs(b.endAt).isAfter(now));

  // Every action a guest can take on one booking, in the order they'd reach
  // for them. The same buttons feed the mobile card and the desktop list --
  // they used to be styled differently by device (see actionButtonProps for
  // why that was backwards), so this no longer takes a variant.
  // Two groups -- see BookingActionsRow, and IncomingBookings' own identical
  // split on the host side.
  const buildActions = (booking: IBooking): { neutral: React.ReactNode[]; risky: React.ReactNode[] } => {
    const actions: React.ReactNode[] = [];
    const risky: React.ReactNode[] = [];
    // First action on the card on purpose: "where is my machine and where is
    // my money" is the question a guest opens this list to answer, and the
    // timeline page is the only place that answers it (see
    // TIMELINE_VISIBLE_STATUSES for why Pending is the one status left out).
    if (TIMELINE_VISIBLE_STATUSES.includes(booking.status)) {
      actions.push(
        <Button key="timeline" size="small" icon={<IconRoute size={14} />} {...actionButtonProps} onClick={() => navigate(`/heavy-duty-machinery/bookings/${booking.id}`)}>
          {translate(t, TRANSLATIONS.BOOKING_TIMELINE_VIEW_BUTTON)}
        </Button>
      );
    }
    // The thread is open from the moment a quote exists until the rental is
    // over -- exactly the statuses the backend lets a message through on
    // (ConversationsController.SendMessage, and CHAT_ENABLED_STATUSES which
    // mirrors it). It used to be offered only on Confirmed/Completed, which
    // left a booking waiting on payment as the one state with a live thread
    // and no way in. AwaitingQuote is excluded here because its own branch
    // below places the same button among the quote decisions, where it
    // belongs.
    if (booking.status !== 'AwaitingQuote' && CHAT_ENABLED_STATUSES.includes(booking.status)) {
      actions.push(
        <Button key="chat" size="small" icon={<IconMessage size={14} />} {...actionButtonProps} onClick={() => navigate(`/heavy-duty-machinery/messages?bookingId=${booking.id}`)}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_BUTTON)}
        </Button>
      );
    }
    if (booking.status === 'Confirmed' || booking.status === 'Completed') {
      actions.push(
        <Button
          key="invoice"
          size="small"
          icon={<IconReceipt2 size={14} />}
          loading={downloadingInvoiceId === booking.id}
          {...actionButtonProps}
          onClick={() => handleDownloadInvoice(booking.id)}
        >
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INVOICE_BUTTON)}
        </Button>
      );
    }
    // Fase 6: guest-submittable CheckOut only for a dry-hire booking (no
    // Operator involved) -- a wet-hire guest never operated the machine
    // themselves, so only the host can report on it (see CheckInOutController's
    // own doc comment on the backend and IncomingBookings' matching button).
    if ((booking.status === 'Confirmed' || booking.status === 'Completed') && !booking.operatorId) {
      actions.push(
        <Button key="check-out" size="small" {...actionButtonProps} onClick={() => navigate(`/heavy-duty-machinery/bookings/${booking.id}/check-in-out/CheckOut`)}>
          {translate(t, TRANSLATIONS.CHECK_IN_OUT_CHECK_OUT_BUTTON)}
        </Button>
      );
    }
    // Reserva diferida, still under negotiation. The guest had nothing here
    // at all: they could see a row saying "presupuesto pendiente" and a
    // total, with no way to look at what that total was made of and no way
    // to withdraw. Mirrors IncomingBookings' own AwaitingQuote branch, and
    // opens the same sheet -- "Ver presupuesto" is the primary action for
    // the same reason it is there: accepting a price you can't inspect
    // isn't a decision.
    if (booking.status === 'AwaitingQuote') {
      actions.push(
        <Button
          key="view-quote"
          type="primary"
          size="small"
          icon={<IconFileInvoice size={14} />}
          {...actionButtonProps}
          onClick={() => setQuoteBooking(booking)}
        >
          {translate(t, TRANSLATIONS.QUOTE_BREAKDOWN_VIEW_BUTTON)}
        </Button>,
        // The negotiation actually happens in the thread, and this is the
        // one place the guest can push back on a price without cancelling
        // the whole thing.
        <Button
          key="chat"
          size="small"
          icon={<IconMessage size={14} />}
          {...actionButtonProps}
          onClick={() => navigate(`/heavy-duty-machinery/messages?bookingId=${booking.id}`)}
        >
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_CHAT_BUTTON)}
        </Button>
      );
      risky.push(
        <Button
          key="cancel-quote"
          danger
          size="small"
          {...actionButtonProps}
          loading={cancellingQuoteId === booking.id}
          onClick={() => setCancelQuoteSheetBooking(booking)}
        >
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_BUTTON)}
        </Button>
      );
      return { neutral: actions, risky };
    }

    // A service that will be charged when the work is done asks for a card and
    // takes nothing, so the button cannot say "pagar" -- it would be the one
    // word this screen must not get wrong. The customer has to confirm the
    // mandate with their bank ONCE, now, while they are here: the hold goes on
    // days later, off-session, and nobody will be around to answer a 3-D
    // Secure prompt then. See serviceBookingHelper.
    if (needsCardOnFile(booking)) {
      actions.push(
        <Button
          key="save-card"
          size="small"
          type="primary"
          icon={<IconCreditCard size={14} />}
          {...actionButtonProps}
          loading={startingCardSetupId === booking.id}
          onClick={() => handleStartCardSetup(booking.id)}
        >
          {translate(t, TRANSLATIONS.SERVICE_SAVE_CARD_BUTTON)}
        </Button>
      );
    }

    // AwaitingPayment: reserva diferida, once accepted -- see ResumePayment's
    // own backend doc comment for why the same resume flow covers both.
    if ((booking.status === 'Pending' || booking.status === 'AwaitingPayment') && booking.paymentStatus === 'RequiresPayment') {
      actions.push(
        <Button key="resume-payment" size="small" type="primary" {...actionButtonProps} onClick={() => setResumePaymentBookingId(booking.id)}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_RESUME_PAYMENT_BUTTON)}
        </Button>
      );
    }
    if (canOfferCancel(booking)) {
      risky.push(
        <Button
          key="cancel"
          danger
          size="small"
          {...actionButtonProps}
          loading={cancellingId === booking.id}
          onClick={() => setCancelSheetBooking(booking)}
        >
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_BUTTON)}
        </Button>
      );
    }
    if (booking.status === 'Completed' && !booking.reviewId) {
      actions.push(
        <Button key="review" size="small" {...actionButtonProps} type="primary" onClick={() => setReviewBookingId(booking.id)}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_REVIEW_LEAVE_BUTTON)}
        </Button>
      );
    }
    return { neutral: actions, risky };
  };

  const renderEmptyOrLoading = (emptyTitleKey: string, emptyDescriptionKey: string, showExploreCta?: boolean) => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      );
    }
    return (
      <EmptyState
        icon={<IconCalendarOff size={24} />}
        title={translate(t, emptyTitleKey)}
        description={translate(t, emptyDescriptionKey)}
        action={
          showExploreCta ? (
            <Button type="primary" onClick={() => navigate('/heavy-duty-machinery')}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_TITLE)}
            </Button>
          ) : undefined
        }
      />
    );
  };

  // Both sizes. `pastList` mutes every card in it regardless of status
  // (they're all already in the past by definition), on top of muting any
  // individually cancelled booking in either list.
  //
  // Desktop used to render a separate antd List instead: the same five
  // fields written out a second time as divider-separated rows with no
  // container, so where one booking ended and the next began was a hairline.
  const renderCardList = (items: IBooking[], emptyTitleKey: string, emptyDescriptionKey: string, showExploreCta: boolean | undefined, pastList: boolean) => {
    if (loading) return renderEmptyOrLoading(emptyTitleKey, emptyDescriptionKey, showExploreCta);
    // A narrow-but-active status filter reads as a different situation than
    // genuinely having no bookings -- a lighter inline message instead of
    // the full empty-state illustration + "go explore" CTA, which wouldn't
    // make sense here (the guest already has bookings, just not ones
    // matching what's currently checked).
    if (items.length === 0 && statusFilter.length > 0) {
      return (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKINGS_FILTER_NO_MATCHES)}</Text>
        </div>
      );
    }
    if (items.length === 0) return renderEmptyOrLoading(emptyTitleKey, emptyDescriptionKey, showExploreCta);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((booking) => (
          <BookingCard
            key={booking.id}
            title={booking.heavyDutyMachineryTitle}
            thumbnailUrl={booking.heavyDutyMachineryThumbnailUrl}
            reference={booking.reference}
            status={booking.status}
            dateRangeLabel={`${dayjs(booking.startAt).format('DD/MM/YYYY HH:mm')} – ${dayjs(booking.endAt).format('DD/MM/YYYY HH:mm')}`}
            priceLabel={`${booking.totalPrice.toFixed(2)}€`}
            extraLine={
              booking.cancellationReason
                ? `${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_HOST_CANCEL_REASON_LABEL)}: ${booking.cancellationReason}`
                : // Above the address, because it answers a question the
                  // customer is about to ask somebody: their bank is showing
                  // them a pending charge for a job nobody has started, and
                  // left unexplained the first thing they do is write in asking
                  // why they have been billed. Said here, on the row, rather
                  // than only in the notification they may have swiped away.
                  isServiceHoldPlaced(booking)
                  ? translate(t, TRANSLATIONS.SERVICE_HOLD_NOT_A_CHARGE)
                  : booking.privateAddress
                    ? `${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ADDRESS_LABEL)}: ${booking.privateAddress}`
                    : undefined
            }
            muted={pastList || booking.status === 'Cancelled'}
            actions={<BookingActionsRow {...buildActions(booking)} />}
          />
        ))}
      </div>
    );
  };

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 900 }}>
        {/* No page title: the nav item that brought you here already says
            "Mis reservas", and on a phone the repeat cost a fifth of the
            screen before the first card. */}
        {!showingHostView && activeBooking && <ActiveRentalCard booking={activeBooking} />}

        {showHostToggle && (
          <SegmentedPillToggle
            value={mobileView}
            onChange={setMobileView}
            options={[
              { value: 'guest', label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKINGS_TOGGLE_GUEST) },
              {
                value: 'host',
                label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKINGS_TOGGLE_HOST),
                badge: <NavBadge count={unseenCount} />,
              },
            ]}
          />
        )}

        {/* Above the guest/host split, and on both sizes: the host view
            brings its own (see IncomingBookings). */}
        {!showingHostView && <BookingStatusFilter value={statusFilter} onChange={setStatusFilter} />}

        {showingHostView ? (
          <IncomingBookings embedded />
        ) : isMobile ? (
          // Mobile: pill tabs and cards. The coral used to be scoped here by
          // a local ConfigProvider, because the app-wide primary was black;
          // it is the brand colour now (see theme/appTheme), so this is a
          // plain fragment and desktop gets the same accent instead of being
          // deliberately left out of it.
          <>
            <MobilePillTabs value={mobileTab} onChange={setMobileTab} />
            {mobileTab === 'upcoming'
              ? renderCardList(
                  upcoming,
                  TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_EMPTY_UPCOMING,
                  TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_EMPTY_UPCOMING_DESCRIPTION,
                  true,
                  false
                )
              : renderCardList(past, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_EMPTY_PAST, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_EMPTY_PAST_DESCRIPTION, false, true)}
          </>
        ) : (
          <Tabs
            items={[
              {
                key: 'upcoming',
                label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_TAB_UPCOMING),
                children: renderCardList(
                  upcoming,
                  TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_EMPTY_UPCOMING,
                  TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_EMPTY_UPCOMING_DESCRIPTION,
                  true,
                  false
                ),
              },
              {
                key: 'past',
                label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_TAB_PAST),
                children: renderCardList(past, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_EMPTY_PAST, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MY_BOOKINGS_EMPTY_PAST_DESCRIPTION, false, true),
              },
            ]}
          />
        )}
      </div>

      {reviewBookingId && (
        <ReviewModal
          bookingId={reviewBookingId}
          open
          onClose={() => setReviewBookingId(undefined)}
          onSubmitted={(review) =>
            setBookings((prev) =>
              prev.map((b) => (b.id === reviewBookingId ? { ...b, reviewId: review.id, reviewHasHostResponse: false } : b))
            )
          }
        />
      )}

      {resumePaymentBookingId && (
        <ResumePaymentModal
          bookingId={resumePaymentBookingId}
          open
          onClose={() => setResumePaymentBookingId(undefined)}
        />
      )}

      {/* Same sheet the host reads from IncomingBookings. Accept is offered
          only once the host has actually countered (quoteHelper) -- at v1
          the amount is still the automatic one and there is nothing to
          accept -- but Cancelar solicitud always is, so the sheet is never
          a dead end: it is where this booking is actually read, and an
          empty footer there looked like a broken screen. */}
      <QuoteBreakdownSheet
        open={!!quoteBooking}
        booking={quoteBooking}
        userId={userId ?? undefined}
        onClose={() => setQuoteBooking(undefined)}
        accepting={acceptingQuoteId === quoteBooking?.id}
        onAccept={
          quoteBooking && canAcceptQuote(quoteBooking, userId ?? undefined)
            ? async () => {
                await handleAcceptQuote(quoteBooking.id);
                setQuoteBooking(undefined);
              }
            : undefined
        }
        onCancel={
          quoteBooking
            ? () => {
                // Straight to the same confirmation the list card opens --
                // cancelling from inside the breakdown should feel like the
                // same action, not a second one.
                setCancelQuoteSheetBooking(quoteBooking);
                setQuoteBooking(undefined);
              }
            : undefined
        }
      />

      {/* Not dismissible by clicking away: a guest who closes this has a
          confirmed booking with no usable card, and will find out days later
          through a notification instead of now. */}
      <Modal
        open={!!saveCardFor}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        onCancel={() => setSaveCardFor(undefined)}
      >
        {saveCardFor && (
          <SaveCardStep
            setupClientSecret={saveCardFor.clientSecret}
            onSaved={() => {
              setSaveCardFor(undefined);
              message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_QUOTE_ACCEPT_SUCCESS));
              loadBookings();
            }}
          />
        )}
      </Modal>

      <ConfirmBottomSheet
        open={!!cancelQuoteSheetBooking}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_CONFIRM)}
        description={cancelQuoteSheetBooking?.heavyDutyMachineryTitle}
        cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
        confirmText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_QUOTE_BUTTON)}
        confirmLoading={!!cancelQuoteSheetBooking && cancellingQuoteId === cancelQuoteSheetBooking.id}
        onConfirm={() => {
          if (cancelQuoteSheetBooking) handleCancelQuote(cancelQuoteSheetBooking.id);
          setCancelQuoteSheetBooking(undefined);
        }}
        onClose={() => setCancelQuoteSheetBooking(undefined)}
      />

      <ConfirmBottomSheet
        open={!!cancelSheetBooking}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_CONFIRM)}
        description={
          cancelSheetBooking
            ? // The money, said out loud before the button is pressed. A
              // guest who learns about the retention afterwards, from their
              // bank, reads a fair charge as a trick -- and the same guest
              // told beforehand simply decides.
              [
                `${cancelSheetBooking.heavyDutyMachineryTitle} · ${dayjs(cancelSheetBooking.startAt).format('D MMM, HH:mm')}`,
                (cancelSheetBooking.cancellationRetention ?? 0) > 0
                  ? t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_REFUND_NOTE, {
                      refund: `${(cancelSheetBooking.totalPrice - (cancelSheetBooking.cancellationRetention ?? 0)).toFixed(2)} ${cancelSheetBooking.hostCurrencyCode}`,
                      retained: `${(cancelSheetBooking.cancellationRetention ?? 0).toFixed(2)} ${cancelSheetBooking.hostCurrencyCode}`,
                    })
                  : '',
              ]
                .filter(Boolean)
                .join('\n')
            : undefined
        }
        cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_KEEP)}
        confirmText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CANCEL_SHEET_CONFIRM)}
        confirmLoading={!!cancelSheetBooking && cancellingId === cancelSheetBooking.id}
        onConfirm={() => {
          if (cancelSheetBooking) handleCancel(cancelSheetBooking.id);
          setCancelSheetBooking(undefined);
        }}
        onClose={() => setCancelSheetBooking(undefined)}
      />
    </AppLayout>
  );
};

export default MyBookings;
